import React, { useState } from 'react';
import {
    Box, Typography, Stack, Chip, IconButton, Tooltip, Switch, Button, CircularProgress, Popover,
    Dialog, DialogContent, DialogActions,
} from '@mui/material';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import WorkHistoryRoundedIcon from '@mui/icons-material/WorkHistoryRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import { PRIMARY, PRIMARY_LIGHT, PRIMARY_BORDER } from '../../theme';
import { solidBtn, ghostBtn } from '../uiKit';
import CareersLinks from './CareersLinks';

const initialsOf = (t) => (t || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('');

function Fact({ icon: Icon, text }) {
    if (!text) return null;
    return (
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', px: 1.4, height: 30, borderRadius: '999px', bgcolor: '#F6F5FC', border: '1px solid #EEEBFA' }}>
            <Icon sx={{ fontSize: 15, color: '#8B87B5' }} />
            <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: '#4B5563' }} noWrap>{text}</Typography>
        </Stack>
    );
}

export default function VacancyCard({
    v, prio, links, siteSet, canPublish = true, pubBusy, pubError, statusBusy, salary,
    onEdit, onDelete, onTogglePublish, onStatus, onCopied,
}) {
    const [linkAnchor, setLinkAnchor] = useState(null);
    const [detail, setDetail] = useState(false);

    const closed = v.status === 'Closed';
    const busy = statusBusy === v.id;
    const hasLinks = Boolean(links?.liveUrl);
    const toggleDisabled = closed || pubBusy === v.id || (!siteSet && !v.isPublished);
    const toggleTip = closed ? 'Only an open vacancy can be published on the careers page.'
        : (!siteSet && !v.isPublished ? 'Set the website address first.' : (v.isPublished ? 'Live on the careers page' : 'Publish on the careers page'));
    const longDesc = (v.description || '').length > 110 || (v.description || '').includes('\n');

    return (
        <Box sx={{
            height: '100%', display: 'flex', flexDirection: 'column', borderRadius: '16px', bgcolor: closed ? '#FBFBFD' : '#fff',
            border: '1px solid #E9E7F5', overflow: 'hidden', opacity: closed ? 0.88 : 1,
            transition: 'box-shadow .15s, border-color .15s', '&:hover': { borderColor: PRIMARY_BORDER, boxShadow: '0 14px 30px -18px rgba(124,92,252,0.55)' },
        }}>
            {/* Header: identity on the left, every action on the right. */}
            <Stack direction="row" spacing={1.4} sx={{ p: 1.8, pb: 1.2, alignItems: 'flex-start' }}>
                <Box sx={{
                    width: 46, height: 46, borderRadius: '14px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 800, fontSize: 15, color: closed ? '#94A3B8' : '#fff',
                    background: closed ? '#EEF1F6' : `linear-gradient(135deg, ${prio.tint} 0%, #9B87FB 100%)`,
                }}>{initialsOf(v.title)}</Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: 15.5, fontWeight: 800, color: '#0F172A', lineHeight: 1.25 }} noWrap>{v.title}</Typography>
                    <Typography sx={{ fontSize: 12, color: '#98A0AE', mt: 0.2 }} noWrap>
                        {[v.department, v.designation].filter(Boolean).join(' · ') || '—'}
                    </Typography>
                    <Stack direction="row" spacing={0.6} sx={{ mt: 0.7 }}>
                        <Chip label={`${v.priority} priority`} size="small" sx={{ height: 19, fontSize: 10, fontWeight: 800, bgcolor: prio.bg, color: prio.color }} />
                        <Chip label={closed ? 'Closed' : 'Open'} size="small" sx={{ height: 19, fontSize: 10, fontWeight: 800, bgcolor: closed ? '#F1F5F9' : '#DCFCE7', color: closed ? '#64748B' : '#15803D' }} />
                    </Stack>
                </Box>

                <Stack sx={{ alignItems: 'flex-end', flexShrink: 0 }}>
                    <Stack direction="row" spacing={0.1} sx={{ alignItems: 'center' }}>
                        <Tooltip arrow title={hasLinks ? 'Careers & job-board links' : 'Publish this vacancy to get job-board links.'}>
                            <span>
                                <IconButton size="small" disabled={!hasLinks || !v.isPublished} onClick={(e) => setLinkAnchor(e.currentTarget)}
                                    sx={{ color: PRIMARY, '&:hover': { bgcolor: PRIMARY_LIGHT } }}>
                                    <LinkRoundedIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip arrow title="Edit"><IconButton size="small" onClick={onEdit} sx={{ color: '#94A3B8', '&:hover': { color: PRIMARY, bgcolor: PRIMARY_LIGHT } }}><EditRoundedIcon sx={{ fontSize: 17 }} /></IconButton></Tooltip>
                        <Tooltip arrow title="Remove"><IconButton size="small" onClick={onDelete} sx={{ color: '#94A3B8', '&:hover': { color: '#E11D48', bgcolor: '#FEF2F2' } }}><DeleteOutlineRoundedIcon sx={{ fontSize: 17 }} /></IconButton></Tooltip>
                    </Stack>
                    {canPublish && <Tooltip arrow title={toggleTip}>
                        <span>
                            <Stack direction="row" spacing={0.3} sx={{ alignItems: 'center' }}>
                                {pubBusy === v.id
                                    ? <CircularProgress size={14} sx={{ mr: 0.5 }} />
                                    : <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: v.isPublished ? '#15803D' : '#98A0AE' }}>{v.isPublished ? 'LIVE' : 'OFF'}</Typography>}
                                <Switch size="small" checked={v.isPublished} disabled={toggleDisabled} onChange={onTogglePublish} />
                            </Stack>
                        </span>
                    </Tooltip>}
                </Stack>
            </Stack>
            {pubError && <Typography sx={{ fontSize: 11.5, color: '#E11D48', px: 1.8, pb: 0.6 }}>{pubError}</Typography>}

            <Box sx={{ mx: 1.8, mt: 0.6, pt: 1.6, borderTop: '1px solid #F1F0F9', flexGrow: 1 }}>
                {/* Hiring status on one line, with a thin progress bar under it. */}
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 0.4 }}>
                    <GroupsRoundedIcon sx={{ fontSize: 16, color: '#64748B' }} />
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0F172A' }}>{v.filled}/{v.openings} hired</Typography>
                    <Typography sx={{ fontSize: 13, color: '#64748B' }}>· {v.openings} {v.openings === 1 ? 'opening' : 'openings'}</Typography>
                </Stack>

                {/* Label / value grid, two columns. */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 2, rowGap: 1.8, mt: 2 }}>
                    {[
                        ['Employment', v.employmentType],
                        ['Experience', v.experience],
                        ['Location', v.location],
                        ['Salary', salary],
                    ].map(([label, value]) => (
                        <Box key={label} sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#8B93A1', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</Typography>
                            <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: '#0F172A', mt: 0.2 }} noWrap>{value || '—'}</Typography>
                        </Box>
                    ))}
                </Box>

                <Box sx={{ mt: 2 }}>
                    {v.description ? (
                        <Typography sx={{ fontSize: 13, color: '#475569', lineHeight: 1.55, whiteSpace: longDesc ? 'normal' : 'pre-line' }}>
                            {longDesc ? `${v.description.replace(/\s+/g, ' ').slice(0, 75).trimEnd()}… ` : v.description}
                            {longDesc && (
                                <Box component="span" role="button" onClick={() => setDetail(true)} sx={{ fontWeight: 800, color: PRIMARY, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                                    Read more
                                </Box>
                            )}
                        </Typography>
                    ) : (
                        <Typography sx={{ fontSize: 12.5, color: '#C4C9D4' }}>No description added.</Typography>
                    )}
                </Box>
            </Box>

            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', px: 1.8, py: 1.6, mt: 1.6, borderTop: '1px solid #F1F0F9' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {v.hiringManager && <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0F172A' }} noWrap>{v.hiringManager}</Typography>}
                    <Typography sx={{ fontSize: 11.5, color: '#98A0AE', mt: v.hiringManager ? 0.2 : 0 }} noWrap>
                        {closed ? (v.closedOn ? `Closed ${v.closedOn}` : 'Closed') : (v.postedOn ? `Posted ${v.postedOn}` : '')}
                    </Typography>
                </Box>
                <Button onClick={() => onStatus(closed ? 'Open' : 'Closed')} disabled={busy}
                    sx={{ ...ghostBtn, height: 34, px: 1.8, fontSize: 12.5, borderRadius: '999px' }}>
                    {busy ? <CircularProgress size={14} /> : (closed ? 'Reopen Job' : 'Close Job')}
                </Button>
            </Stack>

            <Popover open={Boolean(linkAnchor)} anchorEl={linkAnchor} onClose={() => setLinkAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { p: 1.6, width: 380, maxWidth: '92vw', borderRadius: '12px' } } }}>
                <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#0F172A', mb: 1 }}>Share this vacancy</Typography>
                <CareersLinks links={links} onCopied={onCopied} />
            </Popover>

            <Dialog open={detail} onClose={() => setDetail(false)} maxWidth="sm" fullWidth slotProps={{ paper: { sx: { borderRadius: '14px' } } }}>
                <DialogContent>
                    <Typography sx={{ fontSize: 19, fontWeight: 800, color: '#0F172A' }}>{v.title}</Typography>
                    <Typography sx={{ fontSize: 12.5, color: '#98A0AE', mb: 1.4 }}>{[v.department, v.designation].filter(Boolean).join(' · ')}</Typography>
                    <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.7, mb: 2 }}>
                        <Fact icon={BadgeRoundedIcon} text={v.employmentType} />
                        <Fact icon={WorkHistoryRoundedIcon} text={v.experience} />
                        <Fact icon={PlaceRoundedIcon} text={v.location} />
                        <Fact icon={PaymentsRoundedIcon} text={salary} />
                    </Stack>
                    <Typography sx={{ fontSize: 13.5, color: '#334155', lineHeight: 1.7, whiteSpace: 'pre-line' }}>{v.description}</Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDetail(false)} sx={{ ...solidBtn, height: 38, px: 2.2 }}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
